import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";

import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import {
  AlarmSeverity,
  ComparisonDirection,
  FieldTrigger,
  NotificationChannel,
} from "../types/alarmInterface";

const channelOptions: NotificationChannel[] = ["IN_APP", "EMAIL", "WHATSAPP", "PUSH"];

const AddFieldAlarmView = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const triggerId = searchParams.get("trigger");
  const api = useAxios();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    metric: "MOISTURE",
    trigger_value: "",
    trigger_type: "AVERAGE" as "AVERAGE" | "SINGLE_DEVICE",
    comparison_direction: "BELOW" as ComparisonDirection,
    severity: "WARNING" as AlarmSeverity,
    notification_channels: ["IN_APP", "EMAIL"] as NotificationChannel[],
  });

  useEffect(() => {
    if (!triggerId) return;

    api
      .get<FieldTrigger>(`/frontend/alarm-triggers/field/${triggerId}/`)
      .then(({ data }) =>
        setForm({
          metric: data.metric,
          trigger_value: String(data.trigger_value),
          trigger_type: data.trigger_type,
          comparison_direction: data.comparison_direction,
          severity: data.severity,
          notification_channels: data.notification_channels,
        }),
      )
      .catch(() =>
        toast({
          title: <Trans>Unable to load field trigger</Trans>,
          status: "error",
          isClosable: true,
        }),
      );
  }, [triggerId]);

  const save = async () => {
    if (!id) return;
    try {
      const payload = {
        ...form,
        field: Number(id),
        trigger_value: Number(form.trigger_value),
      };
      if (triggerId) {
        await api.put(`/frontend/alarm-triggers/field/${triggerId}/`, payload);
      } else {
        await api.post("/frontend/alarm-triggers/field/", payload);
      }
      toast({
        title: triggerId ? (
          <Trans>Field trigger updated</Trans>
        ) : (
          <Trans>Field trigger created</Trans>
        ),
        status: "success",
        isClosable: true,
      });
      navigate("/alarms");
    } catch {
      toast({
        title: <Trans>Unable to save field trigger</Trans>,
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Navbar>
      <Box maxW="760px" mx="auto" py={8} px={4}>
        <Heading size="lg" mb={2}>
          {triggerId ? <Trans>Edit field alarm</Trans> : <Trans>Add field alarm</Trans>}
        </Heading>
        <Text color="gray.500" mb={6}>
          <Trans>Configure when this field should create an alarm.</Trans>
        </Text>
        <VStack align="stretch" spacing={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Measurement</Trans>
              </FormLabel>
              <Select
                value={form.metric}
                onChange={(event) => setForm({ ...form, metric: event.target.value })}
              >
                <option value="MOISTURE">
                  <Trans>Moisture</Trans>
                </option>
                <option value="MOISTURE_2">
                  <Trans>Soil moisture 2</Trans>
                </option>
                <option value="MOISTURE_3">
                  <Trans>Soil moisture 3</Trans>
                </option>
                <option value="TEMPERATURE">
                  <Trans>Temperature</Trans>
                </option>
                <option value="HUMIDITY">
                  <Trans>Humidity</Trans>
                </option>
                <option value="SOIL_TEMPERATURE">
                  <Trans>Soil temperature</Trans>
                </option>
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Trigger type</Trans>
              </FormLabel>
              <Select
                value={form.trigger_type}
                onChange={(event) =>
                  setForm({
                    ...form,
                    trigger_type: event.target.value as "AVERAGE" | "SINGLE_DEVICE",
                  })
                }
              >
                <option value="AVERAGE">
                  <Trans>Average</Trans>
                </option>
                <option value="SINGLE_DEVICE">
                  <Trans>Single device</Trans>
                </option>
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                {form.trigger_type === "AVERAGE" ? (
                  <Trans>Trigger when average is</Trans>
                ) : (
                  <Trans>Trigger when a device value is</Trans>
                )}
              </FormLabel>
              <Select
                value={form.comparison_direction}
                onChange={(event) =>
                  setForm({
                    ...form,
                    comparison_direction: event.target.value as ComparisonDirection,
                  })
                }
              >
                <option value="BELOW">
                  <Trans>Below</Trans>
                </option>
                <option value="ABOVE">
                  <Trans>Above</Trans>
                </option>
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Threshold</Trans>
              </FormLabel>
              <Input
                type="number"
                value={form.trigger_value}
                onChange={(event) => setForm({ ...form, trigger_value: event.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Trans>Severity</Trans>
              </FormLabel>
              <Select
                value={form.severity}
                onChange={(event) =>
                  setForm({ ...form, severity: event.target.value as AlarmSeverity })
                }
              >
                <option value="WARNING">
                  <Trans>Warning</Trans>
                </option>
                <option value="ERROR">
                  <Trans>Error</Trans>
                </option>
              </Select>
            </FormControl>
          </SimpleGrid>
          <FormControl isRequired>
            <FormLabel>
              <Trans>Notifications</Trans>
            </FormLabel>
            <CheckboxGroup
              value={form.notification_channels}
              onChange={(values) =>
                setForm({ ...form, notification_channels: values as NotificationChannel[] })
              }
            >
              <Stack direction={{ base: "column", md: "row" }} spacing={5}>
                {channelOptions.map((channel) => (
                  <Checkbox
                    key={channel}
                    value={channel}
                    isDisabled={channel === "WHATSAPP" || channel === "PUSH"}
                  >
                    {channel.replaceAll("_", " ")}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </FormControl>
          <Button
            alignSelf="start"
            colorScheme="teal"
            onClick={save}
            isDisabled={!id || !form.trigger_value || !form.notification_channels.length}
          >
            <Trans>Save alarm</Trans>
          </Button>
        </VStack>
      </Box>
    </Navbar>
  );
};

export default AddFieldAlarmView;
